import { Channel } from 'phoenix'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { IframeContext } from '../IframeConn'
import { NotificationsContext } from '../Notifications'
import { UsersManagerContext } from '../UsersManager'
import { WebsocketContext } from '../WebsocketConn'
import {
  getCannedResponsesAndStories,
  getChats,
  getMiddlewareMethods,
  getSettings,
} from './init'
import poolHandler from './poolHandler'
import {
  AutocompleteItem,
  CannedResponse,
  Chat,
  KbTopic,
  MiddlewareCommand,
  MiddlewareCommandResponse,
  Story,
} from './types'
import { getUsersRedirectList, UserOnRedirectList } from './usersRedirectList'
import { chatApi } from './utils'
import MiniSearch from 'minisearch'
import {
  aiCompletionApplied,
  aiCompletionStart,
  handleAiCompletionData,
} from './aiCompletionHandler'
import { notify } from '~/utils/notify'
import { Attachment } from '~/components/common/upload'
import filesize from 'filesize'

export type Settings = {
  [key: number]: {
    forceTagBeforeClose: boolean
  }
}

type State = {
  activeChatMaybe?: Chat
  activeChat: Chat
  chats: Chat[]
  cannedResponses: CannedResponse[]
  stories: Story[]
  settings: Settings
  activateChat: (id?: string) => void
  removeChat: (id?: string) => void
  sendMessage: (content: string, attachments?: Attachment[]) => Promise<void>
  sendMessageAndRedirectToOperator: (
    userSaasId: number,
    content: string
  ) => Promise<void>
  redirectToStory: (story: number) => Promise<void>
  middlewareCommands: MiddlewareCommand[]
  runMiddlewareCommand: (
    name: string,
    params: Record<string, any>
  ) => Promise<MiddlewareCommandResponse>
  loadUsersRedirectList: () => Promise<void>
  sendTyping: (id: string, typing: boolean) => void
  endChat: () => void
  createTicket: () => void
  pokeSession: () => void
  isConnected: boolean
  channel: Channel
  pauseState: boolean
  setPause: (pause: boolean) => void
  readMessages: () => void
  unread: number
  activePauseSupervisor: boolean
  usersOnRedirectList?: UserOnRedirectList[]
  autocompleteSearch: MiniSearch<AutocompleteItem>
  aiCompletionStart: (chatId: string, requestId: string, type: string) => void
  aiCompletionApplied: (chatId: string) => void
  scrollOffset: boolean
  setScrollOffset: (offset: boolean) => void
}

export const ChatContext = React.createContext<State>({
  chats: [],
  activeChat: {} as Chat,
  cannedResponses: [],
  stories: [],
  settings: {},
  activateChat: () => {},
  removeChat: () => {},
  sendMessage: () => new Promise(() => {}),
  sendMessageAndRedirectToOperator: () => new Promise(() => {}),
  redirectToStory: () => new Promise(() => {}),
  loadUsersRedirectList: () => new Promise(() => {}),
  middlewareCommands: [],
  runMiddlewareCommand: () => new Promise(() => {}),
  sendTyping: () => {},
  endChat: () => {},
  createTicket: () => {},
  pokeSession: () => {},
  isConnected: false,
  channel: {} as Channel,
  pauseState: false,
  setPause: () => {},
  readMessages: () => {},
  unread: 0,
  activePauseSupervisor: false,
  autocompleteSearch: undefined as unknown as MiniSearch<AutocompleteItem>,
  aiCompletionStart: () => {},
  aiCompletionApplied: () => {},
  scrollOffset: false,
  setScrollOffset: () => {},
})

type Pool = {
  chatAccountId: number
  commands: any[]
  id: string
  queueId: number
}

type JoinResp = {
  paused: boolean
}

const join = (channel: Channel): Promise<JoinResp> =>
  new Promise((resolve, reject) => {
    channel
      .join()
      .receive('ok', (resp: { resp: JoinResp[] }) => {
        resolve(resp.resp[0])
      })
      .receive('error', (resp: any) => {
        notify('LiveChat connection failed', 'error', {
          autoClose: false,
        })

        reject(resp)
      })
  })

type Props = {
  children: React.ReactNode
}

const ChatConn: React.FC<Props> = ({ children }) => {
  const [isConnected, setConnected] = useState(false)
  const iframeContextState = useContext(IframeContext)
  const notificationsContextState = useContext(NotificationsContext)
  const [chats, setChats] = useState<Chat[]>([])
  const [settings, setSettings] = useState<Settings>({})
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([])
  const [stories, setStories] = useState<Story[]>([])
  const [middlewareCommands, setMiddlewareCommands] = useState<
    MiddlewareCommand[]
  >([])
  const wsContext = useContext(WebsocketContext)
  const channelRef = useRef<Channel>({} as Channel)
  const [activePauseSupervisor, setActiveChatSupervisor] = useState(false)
  // pauza jako null oznacza, że przy logowaniu to SAAS zadecyduje czy włączyć pauzę
  const [pauseState, setPauseState] = useState<null | boolean>(null)
  const [scrollOffset, setScrollOffset] = useState<boolean>(false)
  const { getByXmpp } = useContext(UsersManagerContext)
  const [usersOnRedirectList, setUsersOnRedirectList] =
    useState<UserOnRedirectList[]>()

  const pauseStateRef = useRef(pauseState)
  pauseStateRef.current = pauseState

  const activePauseSupervisorRef = useRef(activePauseSupervisor)
  activePauseSupervisorRef.current = activePauseSupervisor

  const chatsRef = useRef(chats)
  chatsRef.current = chats

  const activeChatRef = useRef<Chat>()
  activeChatRef.current = chats.find(ch => ch.active)

  const autocompleteSearch = useMemo(() => {
    const stopWords = new Set(['and', 'or', 'to', 'in', 'a', 'the'])

    // tego dobrze nie widać, ale zdefiniowanie stop wordsów pomaga

    return new MiniSearch<AutocompleteItem>({
      fields: ['search'],
      storeFields: ['name', 'message', 'originId', 'type'],
      processTerm: term =>
        term.length == 1 || stopWords.has(term)
          ? null
          : term.toLowerCase().removeAccents(),
      searchOptions: {
        processTerm: term => term.toLowerCase().removeAccents(),
      },
    })
  }, [])

  const getChatById = (id: string) => chats.find(chat => chat.id == id)!

  const activateChat = (id: string) =>
    setChats(chats => chats.map(chat => ({ ...chat, active: chat.id == id })))

  const removeChat = (id: string) =>
    setChats(chats => chats.filter(chat => chat.id != id))

  const updateActiveChat = (callback: (chat: Chat) => Chat) =>
    setChats(chats => chats.map(chat => (chat.active ? callback(chat) : chat)))

  const afterSend = () => {
    readMessages()

    updateActiveChat(chat => ({
      ...chat,
      unreadAt: undefined,
    }))
  }

  const sendMessage = async (
    content: string,
    attachments: Attachment[] = []
  ) => {
    const chat = activeChatRef.current!

    const attachmentsImages = attachments.filter(a => a.isImage)
    const attachmentsRest = attachments.filter(a => !a.isImage)

    const imagesContent = attachmentsImages.length
      ? '\n' + attachmentsImages.map(a => `[img]${a.url}[/img]`).join('')
      : ''

    const attachmentsContent = attachmentsRest.length
      ? '[list]' +
        attachmentsRest
          .map(
            a =>
              `[*][url=${a.url}]${a.name}[/url] (${filesize(a.size, {
                standard: 'jedec',
                round: 0,
              })})`
          )
          .join('\n') +
        '[/list]'
      : ''

    await chatApi(channelRef.current!, chat.chatAccountId, {
      module: 'talk',
      method: 'sendOperatorMessage',
      args: [chat.id, content + imagesContent + attachmentsContent],
    })

    afterSend()
  }

  const sendMessageAndRedirectToOperator = async (
    userSaasId: number,
    content: string
  ) => {
    const chat = activeChatRef.current!

    const result = await chatApi(
      channelRef.current as Channel,
      chat.chatAccountId,
      {
        module: 'talk',
        method: 'redirectToOperator',
        args: [chat.id, userSaasId, content],
      }
    )

    // result nic nie zwraca i tak
    // w backendzie w phoeniksie też nie widzę by coś zwracał
    // i w poolu też nic nie wpada
    // nie wiemy, czy się udało przekierowanie czy nie
    // console.log('przekierowanie', result)

    afterSend()
  }

  const redirectToStory = async (story: number) => {
    const chat = activeChatRef.current!

    const result = await chatApi(
      channelRef.current as Channel,
      chat.chatAccountId,
      {
        module: 'talk',
        method: 'redirectToScript',
        args: [chat.id, story],
      }
    )

    afterSend()
  }

  const runMiddlewareCommand = async (
    name: string,
    params: Record<string, any>
  ) => {
    const chat = activeChatRef.current!

    const paramsToSend = Object.entries(params).map(([key, value]) => ({
      name: key,
      value,
    }))

    return await chatApi<MiddlewareCommandResponse>(
      channelRef.current!,
      chat.chatAccountId,
      {
        module: 'talk',
        method: 'callMiddlewareMethod',
        args: [name, chat.id, paramsToSend],
      }
    )
  }

  const loadUsersRedirectList = async () => {
    const items = await getUsersRedirectList(
      activeChatRef.current!,
      channelRef.current!,
      getByXmpp
    )

    setUsersOnRedirectList(items)
  }

  const endChat = () => {
    const chat = activeChatRef.current!

    chatApi(channelRef.current!, chat.chatAccountId, {
      module: 'talk',
      method: 'endOperatorChat',
      args: [chat.id],
    })
  }

  const setPause = (pause: boolean) => {
    setPauseState(pause)

    window.c.chatAccounts.forEach(account => {
      chatApi(channelRef.current!, account.id, {
        module: 'talk',
        method: 'setOperatorPause',
        args: [pause],
      })
    })
  }

  const createTicket = () => {
    const chat = activeChatRef.current!

    if (window.isIframe) {
      iframeContextState.parentRef.current?.createTicket(chat)
    } else {
      alert('works only in iframe')
    }
  }

  const pokeSession = () => {
    const chat = activeChatRef.current!

    chatApi(channelRef.current as Channel, chat.chatAccountId, {
      module: 'talk',
      method: 'pokeOperatorSession',
      args: [chat.id],
    })
  }

  const sendTyping = (id: string, typing: boolean) => {
    const chat = getChatById(id)

    chatApi(channelRef.current as Channel, chat.chatAccountId, {
      module: 'talk',
      method: 'sendOperatorWriting',
      args: [id, !typing],
    })
  }

  const readMessages = () => {
    const chat = activeChatRef.current

    // the chat may be empty when calling readMessages with setTimeout
    if (!chat) {
      return
    }

    const messages = chat.messages
      .filter(message => message.unread)
      .map(message => message.queueId)

    if (!messages.length) {
      return
    }

    updateActiveChat(chat => ({
      ...chat,
      messages: chat.messages.map(m => ({ ...m, unread: false })),
      unread: 0,
    }))

    channelRef.current!.push('read_message', {
      chatAccountId: chat.chatAccountId,
      chatId: chat.id,
      messages,
    })
  }

  useEffect(() => {
    iframeContextState.parentRef.current?.setChatLoading(!isConnected)
  }, [isConnected])

  useEffect(() => {
    const poolReceived = window.c.chatAccounts.reduce(
      (o, acc) => ({ ...o, [acc.id]: [] }),
      {}
    )

    ;(async () => {
      const ch = wsContext.socket.channel(
        'phone_chat:' + window.c.userId,
        () => ({ pause: pauseStateRef.current })
      )

      channelRef.current = ch

      const resp = await join(ch)

      setPauseState(resp.paused)

      setConnected(true)

      const [stories, cannedResponses] = await getCannedResponsesAndStories(ch)

      autocompleteSearch.addAll([...cannedResponses, ...stories])

      setMiddlewareCommands(await getMiddlewareMethods(ch))
      setStories(stories)
      setCannedResponses(cannedResponses)
      setSettings(await getSettings(ch))
      setChats(await getChats(ch))

      ch.onClose(e => {
        console.log('channel close', e)
      })
      ch.onError(e => {
        console.log('channel error', e)
      })

      ch.on('pool', (data: Pool) => {
        const { id, commands, chatAccountId, queueId } = data

        // console.log(commands, queueId)

        ch.push('received', { id, chatAccountId })

        if (!poolReceived[chatAccountId].includes(id)) {
          poolReceived[chatAccountId].push(id)

          if (poolReceived[chatAccountId].length > 10) {
            poolReceived[chatAccountId].shift()
          }

          if (commands.length > 1) {
            // console.log('pool', commands)
          }

          poolHandler(
            ch,
            commands,
            chatAccountId,
            queueId,
            chatsRef.current,
            setChats,
            iframeContextState,
            notificationsContextState,
            activePauseSupervisorRef,
            setActiveChatSupervisor
          )
        }
      })

      ch.on('relogin', () =>
        (async () => {
          console.log('relogin!')

          setChats(await getChats(ch))
        })()
      )

      ch.on('aiCompletion', data => handleAiCompletionData(setChats, data))

      // channel jest jeszcze użyty w Pause.tsx
    })()
  }, [])

  const unread = chats.reduce((prev, curr) => prev + curr.unread, 0)

  useEffect(() => {
    iframeContextState.parentRef.current?.setUnreadCount(unread)
  }, [unread])

  return (
    <ChatContext.Provider
      value={{
        activeChat: activeChatRef.current!,
        activeChatMaybe: activeChatRef.current,
        chats,
        cannedResponses,
        autocompleteSearch,
        stories,
        settings,
        activateChat,
        removeChat,
        sendMessage,
        sendMessageAndRedirectToOperator,
        redirectToStory,
        middlewareCommands,
        runMiddlewareCommand,
        loadUsersRedirectList,
        endChat,
        createTicket,
        pokeSession,
        isConnected,
        sendTyping,
        channel: channelRef.current,
        pauseState: pauseState!,
        setPause,
        readMessages,
        unread,
        activePauseSupervisor,
        usersOnRedirectList,
        aiCompletionStart: aiCompletionStart(setChats),
        aiCompletionApplied: aiCompletionApplied(setChats),
        scrollOffset,
        setScrollOffset,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

export default ChatConn
